import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Input, Card, Steps, Tag, message, Select, Divider, Result, Typography, Space, Grid, Alert, Radio, Spin, QRCode,
} from 'antd';
import {
  ArrowLeftOutlined, ArrowRightOutlined, ToolOutlined,
  CheckCircleFilled, GoogleOutlined, CopyOutlined, ShopOutlined, EnvironmentOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;
const { TextArea } = Input;

const API_BASE = process.env.REACT_APP_API_BASE || '';

// localStorage 暫存：填寫進度與內容（刷新後留在同一步、資料還在）
const DRAFT_KEY = 'repair_order_draft_v1';
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24 小時後失效
const loadDraft = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(DRAFT_KEY));
    if (!raw || typeof raw !== 'object') return {};
    if (raw.savedAt && Date.now() - raw.savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(DRAFT_KEY);
      return {};
    }
    return raw;
  } catch { return {}; }
};

const getToken = () => { try { return localStorage.getItem('customer_token'); } catch { return null; } };

// 手圍尺寸（比照 CustomOrder：13 ~ 22，每 0.5 一段）
const WRIST_SIZES = [];
for (let i = 13; i <= 22; i += 0.5) WRIST_SIZES.push(i);

// 維修項目定義
const REPAIR_ITEMS = [
  { key: 'elastic', label: '更換彈力線', desc: '彈力線鬆脫、老化或斷裂' },
  { key: 'resize', label: '尺寸過大或過小', desc: '重新調整手圍尺寸' },
  { key: 'crystal', label: '水晶破裂或掉珠', desc: '更換破損或遺失的水晶珠' },
  { key: 'metal', label: '更換金屬配件', desc: '扣頭、隔珠等金屬零件更換' },
  { key: 'other', label: '其他', desc: '其他維修需求（請描述）' },
];

// 選項卡（比照 CustomOrder 的 OptionCard；支援單選 radio 與多選 check 標記）
function OptionCard({ selected, onClick, children, style }) {
  return (
    <Card
      hoverable
      onClick={onClick}
      style={{
        border: selected ? '2px solid var(--brand-primary)' : '2px solid #f0f0f0',
        background: selected ? '#F4F5FB' : '#fff',
        cursor: 'pointer',
        transition: 'all 0.3s',
        position: 'relative',
        ...style,
      }}
      bodyStyle={{ padding: '16px 20px' }}
    >
      {children}
      {selected && (
        <CheckCircleFilled style={{ position: 'absolute', top: 8, right: 8, color: 'var(--brand-primary)', fontSize: 18 }} />
      )}
    </Card>
  );
}

export default function RepairApplication() {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();

  // 登入狀態
  const [customer, setCustomer] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // 維修設定（費用、首次免費、寄件到府門市）
  const [config, setConfig] = useState(null);

  // 本站訂單（下拉選一筆）
  const [myOrders, setMyOrders] = useState(null);
  const [myOrdersLoading, setMyOrdersLoading] = useState(false);

  // 表單狀態（初值優先從草稿還原）
  const [draft0] = useState(loadDraft);
  const [currentStep, setCurrentStep] = useState(draft0.currentStep || 0);
  const [source, setSource] = useState(draft0.source ?? null); // 'site' | 'pinkoi'
  const [sourceOrderCode, setSourceOrderCode] = useState(draft0.sourceOrderCode || '');
  const [linkedOrderId, setLinkedOrderId] = useState(draft0.linkedOrderId ?? null);
  const [lockedSource, setLockedSource] = useState(false); // 由 ?order_code= 帶入時鎖定 Step 1

  const [repairItems, setRepairItems] = useState(draft0.repairItems || []);
  const [repairItemsOther, setRepairItemsOther] = useState(draft0.repairItemsOther || '');
  const [currentWrist, setCurrentWrist] = useState(draft0.currentWrist ?? null);
  const [targetWrist, setTargetWrist] = useState(draft0.targetWrist ?? null);

  const [shipBackMethod, setShipBackMethod] = useState(draft0.shipBackMethod || 'post'); // 'post' | 'cvs'
  const [phone, setPhone] = useState(draft0.phone || '');
  const [recipientName, setRecipientName] = useState(draft0.recipientName || '');
  const [recipientPhone, setRecipientPhone] = useState(draft0.recipientPhone || '');
  const [recipientAddress, setRecipientAddress] = useState(draft0.recipientAddress || '');
  const [cvsSubType, setCvsSubType] = useState(draft0.cvsSubType || 'UNIMARTC2C');
  const [cvsStore, setCvsStore] = useState(draft0.cvsStore ?? null); // {id, name, addr}

  const [note, setNote] = useState(draft0.note || '');

  const [submitting, setSubmitting] = useState(false);

  // 綠界付款導回：顯示全家寄件單（寄件代碼 / QR）
  const [payResultCode, setPayResultCode] = useState(null); // 付款成功導回的維修單號
  const [payFailed, setPayFailed] = useState(false);        // 付款失敗 / 取消
  const [paidOrder, setPaidOrder] = useState(null);         // 導回後撈到的維修單（含寄件碼）
  const [inboundPolling, setInboundPolling] = useState(false);

  // ==================== 設定 / 登入導回 ====================

  // 載入維修設定（公開）
  useEffect(() => {
    fetch(`${API_BASE}/api/repairs/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setConfig(d); })
      .catch(() => {});
  }, []);

  // 處理登入 / 選店導回的網址參數，並取得客人資訊
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    let changed = false;

    // Google 登入回來：帶 ctoken
    const ctoken = q.get('ctoken');
    if (ctoken) {
      try { localStorage.setItem('customer_token', ctoken); } catch {}
      q.delete('ctoken'); changed = true;
    }

    // 綠界超商選店回來：帶門市資訊 → 設為寄回門市
    if (q.get('cvs') === '1' && q.get('storeId')) {
      setShipBackMethod('cvs');
      if (q.get('subType')) setCvsSubType(q.get('subType'));
      setCvsStore({
        id: q.get('storeId') || '',
        name: q.get('storeName') || '',
        addr: q.get('storeAddr') || '',
      });
      ['cvs', 'subType', 'storeId', 'storeName', 'storeAddr'].forEach((k) => q.delete(k));
      changed = true;
    }

    // 綠界付款導回：pay_result=1 顯示全家寄件單；其餘（0 / pay_cancel）視為失敗/取消
    const pr = q.get('pay_result');
    const rc = q.get('repair');
    if (pr !== null) {
      if (pr === '1' && rc) setPayResultCode(rc);
      else setPayFailed(true);
      ['pay_result', 'repair'].forEach((k) => q.delete(k));
      changed = true;
    }
    if (q.get('pay_cancel')) { setPayFailed(true); q.delete('pay_cancel'); changed = true; }

    // ?order_code= 帶入：預設本站訂單且帶入單號、鎖定 Step 1
    const oc = q.get('order_code');
    if (oc) {
      setSource('site');
      setSourceOrderCode(oc);
      setLockedSource(true);
      q.delete('order_code');
      changed = true;
    }

    if (changed) {
      const rest = q.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }

    const tok = ctoken || getToken();
    if (!tok) { setCheckingAuth(false); return; }
    fetch(`${API_BASE}/api/auth/google/customer/me`, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((info) => {
        if (info) setCustomer(info);
        else { try { localStorage.removeItem('customer_token'); } catch {} }
      })
      .catch(() => {})
      .finally(() => setCheckingAuth(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 登入後載入自己的訂單（下拉選本站訂單用）
  useEffect(() => {
    if (!customer) { setMyOrders(null); return; }
    const token = getToken();
    if (!token) return;
    setMyOrdersLoading(true);
    fetch(`${API_BASE}/api/bracelet-orders/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setMyOrders(d.items || []))
      .catch(() => setMyOrders([]))
      .finally(() => setMyOrdersLoading(false));
  }, [customer]);

  // 會員若已存手機 → 自動帶入聯絡手機／收件人電話（欄位已有值則不覆蓋，例如草稿還原）
  useEffect(() => {
    if (!customer) return;
    const token = getToken();
    if (!token) return;
    fetch(`${API_BASE}/api/customer/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (!p || !p.phone) return;
        setPhone((cur) => (cur && cur.trim() ? cur : p.phone));
        setRecipientPhone((cur) => (cur && cur.trim() ? cur : p.phone));
      })
      .catch(() => {});
  }, [customer]);

  // 付款導回後：輪詢自己的維修單，等後端建立好全家寄件單（拿到寄件代碼）
  useEffect(() => {
    if (!payResultCode) return undefined;
    const token = getToken();
    if (!token) return undefined;
    let stopped = false;
    let tries = 0;
    setInboundPolling(true);
    const poll = async () => {
      tries += 1;
      try {
        const r = await fetch(`${API_BASE}/api/repairs/my`, { headers: { Authorization: `Bearer ${token}` } });
        const d = r.ok ? await r.json() : { items: [] };
        const o = (d.items || []).find((x) => x.repair_code === payResultCode);
        if (o) setPaidOrder(o);
        if (o && (o.inbound_cvs_payment_no || o.inbound_logistics_status)) { setInboundPolling(false); return; }
      } catch { /* 忽略：續輪詢 */ }
      if (!stopped && tries < 6) { setTimeout(poll, 2500); } else { setInboundPolling(false); }
    };
    poll();
    return () => { stopped = true; };
  }, [payResultCode]);

  // 收件人 email 自動帶入登入者
  const customerEmail = customer?.email || null;

  // ==================== 草稿存回 ====================
  useEffect(() => {
    // 付款導回頁（成功/失敗）不再存草稿，並清掉舊草稿
    if (payResultCode || payFailed) { try { localStorage.removeItem(DRAFT_KEY); } catch {} return; }
    const snapshot = {
      currentStep, source, sourceOrderCode, linkedOrderId,
      repairItems, repairItemsOther, currentWrist, targetWrist,
      shipBackMethod, phone, recipientName, recipientPhone, recipientAddress,
      cvsSubType, cvsStore, note,
      savedAt: Date.now(),
    };
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(snapshot)); } catch {}
  }, [
    currentStep, source, sourceOrderCode, linkedOrderId,
    repairItems, repairItemsOther, currentWrist, targetWrist,
    shipBackMethod, phone, recipientName, recipientPhone, recipientAddress,
    cvsSubType, cvsStore, note, payResultCode, payFailed,
  ]);

  // ==================== 步驟 ====================
  const steps = useMemo(() => ([
    { key: 'source', title: '訂單來源' },
    { key: 'items', title: '維修項目' },
    { key: 'shipping', title: '寄回方式' },
    { key: 'confirm', title: '備註送出' },
  ]), []);
  const currentKey = steps[currentStep]?.key;

  const goNext = useCallback(() => {
    setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
    window.scrollTo(0, 0);
  }, [steps.length]);

  const goPrev = useCallback(() => {
    setCurrentStep((s) => Math.max(s - 1, 0));
    window.scrollTo(0, 0);
  }, []);

  // ==================== 操作 ====================
  const startCustomerLogin = useCallback(() => {
    // 保留 order_code（若有），登入回來還能帶入
    const oc = new URLSearchParams(window.location.search).get('order_code') || sourceOrderCode;
    const next = oc ? `/repair?order_code=${encodeURIComponent(oc)}` : '/repair';
    window.location.href = `${API_BASE}/api/auth/google/customer/login?next=${encodeURIComponent(next)}`;
  }, [sourceOrderCode]);

  const toggleRepairItem = useCallback((key) => {
    setRepairItems((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, []);

  // 選本站訂單（下拉）：帶入 order_code 當來源單號、id 當 linked_order_id
  const onPickSiteOrder = useCallback((orderId) => {
    const o = (myOrders || []).find((x) => x.id === orderId);
    if (!o) { setLinkedOrderId(null); setSourceOrderCode(''); return; }
    setLinkedOrderId(o.id);
    setSourceOrderCode(o.order_code || '');
  }, [myOrders]);

  // 綠界電子地圖選店（寄回門市）
  const pickCvsStore = useCallback(() => {
    window.location.href = `${API_BASE}/api/logistics/ecpay/map?sub_type=${cvsSubType || 'UNIMARTC2C'}&back=repair`;
  }, [cvsSubType]);

  // 各步驟可否往下一步
  const canNext = useMemo(() => {
    if (currentKey === 'source') {
      if (source === 'site') return !!sourceOrderCode;
      if (source === 'pinkoi') return !!sourceOrderCode.trim();
      return false;
    }
    if (currentKey === 'items') {
      if (repairItems.length === 0) return false;
      if (repairItems.includes('resize') && (!currentWrist || !targetWrist)) return false;
      if (repairItems.includes('other') && !repairItemsOther.trim()) return false;
      return true;
    }
    if (currentKey === 'shipping') {
      if (!recipientName.trim() || !recipientPhone.trim() || !phone.trim()) return false;
      if (shipBackMethod === 'post') return !!recipientAddress.trim();
      if (shipBackMethod === 'cvs') return !!(cvsStore && cvsStore.id);
      return false;
    }
    return true;
  }, [currentKey, source, sourceOrderCode, repairItems, currentWrist, targetWrist,
    repairItemsOther, recipientName, recipientPhone, phone, shipBackMethod, recipientAddress, cvsStore]);

  const nextHint = useMemo(() => {
    if (canNext) return null;
    if (currentKey === 'source') return source ? '請填寫訂單編號' : '請選擇訂單來源';
    if (currentKey === 'items') {
      if (repairItems.length === 0) return '請至少選擇一項維修項目';
      if (repairItems.includes('resize') && (!currentWrist || !targetWrist)) return '請填寫目前與期望手圍';
      if (repairItems.includes('other') && !repairItemsOther.trim()) return '請描述其他維修需求';
    }
    if (currentKey === 'shipping') {
      if (!phone.trim()) return '請填寫聯絡手機';
      if (!recipientName.trim() || !recipientPhone.trim()) return '請填寫收件人姓名與電話';
      if (shipBackMethod === 'post' && !recipientAddress.trim()) return '請填寫寄回地址';
      if (shipBackMethod === 'cvs' && !(cvsStore && cvsStore.id)) return '請選擇寄回超商門市';
    }
    return null;
  }, [canNext, currentKey, source, repairItems, currentWrist, targetWrist, repairItemsOther,
    phone, recipientName, recipientPhone, shipBackMethod, recipientAddress, cvsStore]);

  // 送出
  const handleSubmit = useCallback(async () => {
    const token = getToken();
    if (!token) { message.warning('請先登入'); startCustomerLogin(); return; }
    if (!canNext) return;

    setSubmitting(true);
    try {
      const payload = {
        source,
        source_order_code: source === 'site' ? sourceOrderCode : sourceOrderCode.trim(),
        linked_order_id: source === 'site' ? linkedOrderId : null,
        repair_items: repairItems,
        repair_items_other: repairItems.includes('other') ? (repairItemsOther.trim() || null) : null,
        current_wrist: repairItems.includes('resize') ? currentWrist : null,
        target_wrist: repairItems.includes('resize') ? targetWrist : null,
        ship_back_method: shipBackMethod,
        phone: phone.trim(),
        customer_email: customerEmail,
        recipient_name: recipientName.trim(),
        recipient_phone: recipientPhone.trim(),
        note: note.trim() || null,
      };
      if (shipBackMethod === 'post') {
        payload.recipient_address = recipientAddress.trim();
      } else {
        payload.logistics_subtype = cvsSubType;
        payload.cvs_store_id = cvsStore?.id || '';
        payload.cvs_store_name = cvsStore?.name || '';
        payload.cvs_store_address = cvsStore?.addr || '';
      }

      const res = await fetch(`${API_BASE}/api/repairs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        message.error(err.detail || '送出失敗，請稍後再試');
        return;
      }
      const data = await res.json();
      try { localStorage.removeItem(DRAFT_KEY); } catch {}

      // 一律導向綠界付款：至少含全家寄件運費（NT$65），若非首次/非水晶還含維修費。
      // 付款成功後綠界導回 /repair?pay_result=1&repair=... → 顯示全家寄件單（寄件代碼 / QR）。
      window.location.href = `${API_BASE}/api/repairs/ecpay/checkout/${data.id}?ctoken=${encodeURIComponent(token)}`;
    } catch {
      message.error('送出失敗，請稍後再試');
    } finally {
      setSubmitting(false);
    }
  }, [canNext, source, sourceOrderCode, linkedOrderId, repairItems, repairItemsOther,
    currentWrist, targetWrist, shipBackMethod, phone, customerEmail, recipientName,
    recipientPhone, recipientAddress, cvsSubType, cvsStore, note, startCustomerLogin]);

  // ==================== 樣式 ====================
  const containerStyle = {
    maxWidth: 600,
    margin: '0 auto',
    padding: isMobile ? '16px' : '24px',
    position: 'relative',
    zIndex: 1,
  };

  // ==================== render：驗證中 / 未登入 ====================
  if (checkingAuth) {
    return (
      <div style={{ ...containerStyle, textAlign: 'center', paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', marginBottom: 24, paddingTop: 24 }}>
          <ToolOutlined style={{ fontSize: 40, color: 'var(--brand-primary)', marginBottom: 12 }} />
          <Title level={3} style={{ color: '#222A5A', fontWeight: 800 }}>售後維修登記</Title>
          <Text type="secondary">用 Google 登入後，即可為您購買的手鏈申請維修</Text>
        </div>
        <div style={{ textAlign: 'center' }}>
          <Button
            type="primary" size="large" icon={<GoogleOutlined />}
            onClick={startCustomerLogin}
            style={{ height: 48, fontSize: 16 }}
          >
            使用 Google 登入
          </Button>
        </div>
      </div>
    );
  }

  // ==================== render：付款失敗 / 取消 ====================
  if (payFailed) {
    return (
      <div style={containerStyle}>
        <Result
          status="warning"
          title="尚未完成付款"
          subTitle="您的維修申請已建立，但全家寄件運費尚未付款完成。可到「我的維修」找到該單，點選付款連結重新付款。"
          extra={[
            <Button key="orders" onClick={() => navigate('/orders')}>查看我的訂單</Button>,
            <Button key="member" type="primary" onClick={() => { setPayFailed(false); navigate('/member'); }}>回會員中心</Button>,
          ]}
        />
      </div>
    );
  }

  // ==================== render：付款完成 → 顯示全家寄件單（寄件代碼 / QR）====================
  if (payResultCode) {
    const o = paidOrder;
    const code = o?.inbound_cvs_payment_no || null;
    const validation = o?.inbound_cvs_validation_no || null;
    return (
      <div style={containerStyle}>
        <Result
          status="success"
          title="付款完成！"
          subTitle="運費已付。請依下方寄件資訊，把手鏈拿到任一「全家」便利商店寄回給我們。"
        />
        <Card style={{ textAlign: 'center', marginBottom: 16 }}>
          <Text type="secondary">您的維修單號</Text>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--brand-primary)', margin: '8px 0', letterSpacing: 2, fontFamily: 'monospace' }}>
            {payResultCode}
          </div>
          <Button icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(payResultCode); message.success('已複製'); }}>
            複製單號
          </Button>
        </Card>

        {code ? (
          <Card title="全家寄件資訊" style={{ marginBottom: 16 }}>
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              <QRCode value={code} size={168} />
              <div style={{ marginTop: 12 }}>
                <Text type="secondary">寄件代碼</Text>
                <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: 2, fontFamily: 'monospace', color: 'var(--brand-primary)' }}>{code}</div>
              </div>
              {validation && (
                <div style={{ marginTop: 6 }}>
                  <Text type="secondary">驗證碼 </Text>
                  <Text strong style={{ fontFamily: 'monospace', fontSize: 16 }}>{validation}</Text>
                </div>
              )}
              <Button
                style={{ marginTop: 10 }}
                icon={<CopyOutlined />}
                onClick={() => { navigator.clipboard.writeText(code); message.success('已複製寄件代碼'); }}
              >
                複製寄件代碼
              </Button>
            </div>
            <Alert
              type="info"
              showIcon
              message="怎麼寄？"
              description={
                <ol style={{ paddingLeft: 18, margin: 0 }}>
                  <li>到任一「全家」便利商店，找店內的 FamiPort 機台。</li>
                  <li>選「店到店寄件」→ 掃描上方 QR 或輸入寄件代碼{validation ? '與驗證碼' : ''}。</li>
                  <li>列印小白單，連同手鏈交給店員即可（運費已付，無需再付款）。</li>
                </ol>
              }
            />
          </Card>
        ) : inboundPolling ? (
          <Card style={{ textAlign: 'center', marginBottom: 16 }}>
            <Spin />
            <div style={{ marginTop: 10 }}><Text type="secondary">正在為您產生全家寄件單，請稍候…</Text></div>
          </Card>
        ) : (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="寄件單產生中"
            description={
              <div>
                {o?.inbound_logistics_status
                  ? <div style={{ marginBottom: 4 }}>目前狀態：{o.inbound_logistics_status}</div>
                  : null}
                稍後請到「我的維修」查看寄件代碼；若持續未顯示，我們會以 Email／LINE 通知您。
              </div>
            }
          />
        )}

        <Space style={{ width: '100%', justifyContent: 'center' }}>
          <Button onClick={() => navigate('/orders')}>查看我的訂單</Button>
          <Button type="primary" onClick={() => { setPayResultCode(null); navigate('/member'); }}>回會員中心</Button>
        </Space>
      </div>
    );
  }

  // ==================== 各步驟內容 ====================
  const renderSource = () => {
    if (lockedSource) {
      return (
        <div>
          <Title level={4} style={{ textAlign: 'center', marginBottom: 8 }}>維修的訂單</Title>
          <Paragraph style={{ textAlign: 'center', color: '#595959', marginBottom: 20 }}>
            以下為您要申請維修的訂單，確認無誤即可繼續。
          </Paragraph>
          <Card style={{ border: '2px solid var(--brand-primary)', background: '#F4F5FB' }}>
            <Space direction="vertical" size={4}>
              <Text type="secondary">本站訂單編號</Text>
              <Tag color="geekblue" style={{ fontSize: 15, fontWeight: 700, padding: '4px 10px' }}>{sourceOrderCode}</Tag>
            </Space>
          </Card>
          <Button type="link" style={{ marginTop: 12, padding: 0 }} onClick={() => setLockedSource(false)}>
            改用其他訂單
          </Button>
        </div>
      );
    }
    return (
      <div>
        <Title level={4} style={{ textAlign: 'center', marginBottom: 8 }}>選擇訂單來源</Title>
        <Paragraph style={{ textAlign: 'center', color: '#595959', marginBottom: 20 }}>
          請選擇您購買手鏈的來源，方便我們對應訂單資料
        </Paragraph>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <OptionCard selected={source === 'site'} onClick={() => { setSource('site'); }}>
            <Space>
              <span style={{ fontSize: 26 }}>🛍️</span>
              <div>
                <Text strong style={{ fontSize: 16 }}>本站訂單</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 13 }}>在星河耀眼官網購買，從下拉選單挑選訂單</Text>
              </div>
            </Space>
          </OptionCard>
          {source === 'site' && (
            <Card size="small" style={{ background: '#fafafa', border: '1px solid #E5E7F5' }}>
              <Text strong style={{ fontSize: 13 }}>選擇要維修的訂單</Text>
              <Select
                style={{ width: '100%', marginTop: 6 }}
                size="large"
                loading={myOrdersLoading}
                placeholder="選擇本站訂單"
                value={linkedOrderId ?? undefined}
                onChange={onPickSiteOrder}
                notFoundContent={myOrdersLoading ? <Spin size="small" /> : '尚無已完成的訂單'}
                options={(myOrders || [])
                  .filter((o) => o.status === 'completed') // 僅已完成的訂單可申請售後
                  .map((o) => ({
                    value: o.id,
                    label: `${o.order_code}${o.selected_bracelet_name ? `（${o.selected_bracelet_name}）` : ''}`,
                  }))}
              />
            </Card>
          )}

          <OptionCard selected={source === 'pinkoi'} onClick={() => { setSource('pinkoi'); setLinkedOrderId(null); if (source !== 'pinkoi') setSourceOrderCode(''); }}>
            <Space>
              <span style={{ fontSize: 26 }}>🧾</span>
              <div>
                <Text strong style={{ fontSize: 16 }}>Pinkoi 訂單</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 13 }}>在 Pinkoi 購買，手動輸入訂單編號</Text>
              </div>
            </Space>
          </OptionCard>
          {source === 'pinkoi' && (
            <Card size="small" style={{ background: '#fafafa', border: '1px solid #E5E7F5' }}>
              <Text strong style={{ fontSize: 13 }}>Pinkoi 訂單編號</Text>
              <Input
                style={{ marginTop: 6 }}
                size="large"
                placeholder="請輸入 Pinkoi 訂單編號"
                value={sourceOrderCode}
                onChange={(e) => setSourceOrderCode(e.target.value)}
                allowClear
              />
            </Card>
          )}
        </Space>
      </div>
    );
  };

  const renderItems = () => (
    <div>
      <Title level={4} style={{ textAlign: 'center', marginBottom: 8 }}>選擇維修項目</Title>
      <Paragraph style={{ textAlign: 'center', color: '#595959', marginBottom: 20 }}>
        可複選，請勾選所有需要處理的項目
      </Paragraph>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {REPAIR_ITEMS.map((it) => (
          <div key={it.key}>
            <OptionCard selected={repairItems.includes(it.key)} onClick={() => toggleRepairItem(it.key)}>
              <div>
                <Text strong style={{ fontSize: 16 }}>{it.label}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 13 }}>{it.desc}</Text>
              </div>
            </OptionCard>

            {/* 水晶破裂/掉珠：可能酌收費用提示 */}
            {it.key === 'crystal' && repairItems.includes('crystal') && (
              <Alert
                type="info"
                showIcon
                style={{ margin: '8px 0 4px' }}
                message="關於水晶維修費用"
                description="此項目將於我們收到手鏈評估後，可能依實際水晶狀況酌收費用；屆時會以綠界付款連結另行通知，確認後才需付款。"
              />
            )}

            {/* 尺寸調整：展開目前 / 期望手圍 */}
            {it.key === 'resize' && repairItems.includes('resize') && (
              <Card size="small" style={{ margin: '8px 0 4px', background: '#fafafa', border: '1px solid #E5E7F5' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <Text strong style={{ fontSize: 13 }}>目前手圍</Text>
                    <Select
                      style={{ width: '100%', marginTop: 6 }} size="large" placeholder="目前 (cm)"
                      value={currentWrist} onChange={setCurrentWrist}
                      options={WRIST_SIZES.map((s) => ({ value: s, label: `${s} cm` }))}
                    />
                  </div>
                  <div>
                    <Text strong style={{ fontSize: 13 }}>期望手圍</Text>
                    <Select
                      style={{ width: '100%', marginTop: 6 }} size="large" placeholder="期望 (cm)"
                      value={targetWrist} onChange={setTargetWrist}
                      options={WRIST_SIZES.map((s) => ({ value: s, label: `${s} cm` }))}
                    />
                  </div>
                </div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                  不確定手圍？用軟尺或紙條繞手腕一圈量出長度即為手圍。
                </Text>
              </Card>
            )}

            {/* 其他：展開文字描述 */}
            {it.key === 'other' && repairItems.includes('other') && (
              <Card size="small" style={{ margin: '8px 0 4px', background: '#fafafa', border: '1px solid #E5E7F5' }}>
                <Text strong style={{ fontSize: 13 }}>其他維修需求說明</Text>
                <TextArea
                  style={{ marginTop: 6 }}
                  rows={3}
                  placeholder="請描述您的維修需求"
                  value={repairItemsOther}
                  onChange={(e) => setRepairItemsOther(e.target.value)}
                  maxLength={500}
                  showCount
                />
              </Card>
            )}
          </div>
        ))}
      </Space>
    </div>
  );

  const CHAIN_LABEL = { UNIMARTC2C: '7-11', FAMIC2C: '全家', HILIFEC2C: '萊爾富', OKMARTC2C: 'OK' };

  const renderShipping = () => (
    <div>
      <Title level={4} style={{ textAlign: 'center', marginBottom: 8 }}>寄回方式與收件資訊</Title>

      {/* 全家寄件說明：付款後系統產生寄件單，客人到全家掃碼寄回 */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="手鏈怎麼寄回來？"
        description={
          <div>
            送出後付款{config?.shipping_fee != null ? <>（含全家寄件運費 <Text strong>NT$ {config.shipping_fee}</Text>）</> : ''}，
            系統會自動產生一張<Text strong>全家店到店寄件單</Text>；
            您帶著手鏈到任一「全家」，用 FamiPort 掃描 QR／輸入寄件代碼即可寄回，運費已付、無需再付現。
          </div>
        }
      />

      <Divider style={{ margin: '8px 0 16px' }}>維修完成後寄回給您</Divider>

      {/* 寄回方式 */}
      <Radio.Group
        value={shipBackMethod}
        onChange={(e) => setShipBackMethod(e.target.value)}
        style={{ width: '100%', marginBottom: 16 }}
        optionType="button"
        buttonStyle="solid"
      >
        <Radio.Button value="post" style={{ width: '50%', textAlign: 'center' }}>
          <EnvironmentOutlined /> 郵局掛號
        </Radio.Button>
        <Radio.Button value="cvs" style={{ width: '50%', textAlign: 'center' }}>
          <ShopOutlined /> 超商店到店
        </Radio.Button>
      </Radio.Group>

      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Text strong style={{ fontSize: 13 }}>收件人姓名</Text>
          <Input style={{ marginTop: 6 }} size="large" placeholder="收件人姓名"
            value={recipientName} onChange={(e) => setRecipientName(e.target.value)} />
        </div>
        <div>
          <Text strong style={{ fontSize: 13 }}>收件人電話</Text>
          <Input style={{ marginTop: 6 }} size="large" placeholder="收件人聯絡電話"
            value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)} />
        </div>

        {shipBackMethod === 'post' && (
          <div>
            <Text strong style={{ fontSize: 13 }}>寄回地址</Text>
            <Input style={{ marginTop: 6 }} size="large" placeholder="完整寄回地址（含郵遞區號）"
              value={recipientAddress} onChange={(e) => setRecipientAddress(e.target.value)} />
          </div>
        )}

        {shipBackMethod === 'cvs' && (
          <div>
            <Text strong style={{ fontSize: 13 }}>寄回超商門市</Text>
            <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
              <Select
                style={{ width: 120 }} size="large"
                value={cvsSubType} onChange={setCvsSubType}
                options={Object.entries(CHAIN_LABEL).map(([v, l]) => ({ value: v, label: l }))}
              />
              <Button size="large" icon={<ShopOutlined />} onClick={pickCvsStore}>選擇門市</Button>
            </div>
            {cvsStore && cvsStore.id && (
              <Alert
                style={{ marginTop: 8 }}
                type="success"
                message={`已選門市：${cvsStore.name}`}
                description={cvsStore.addr}
              />
            )}
          </div>
        )}

        <Divider style={{ margin: '4px 0' }} />

        <div>
          <Text strong style={{ fontSize: 13 }}>聯絡手機</Text>
          <Input style={{ marginTop: 6 }} size="large" placeholder="方便聯繫的手機號碼"
            value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        {customerEmail && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            聯絡信箱將使用您的登入帳號：{customerEmail}
          </Text>
        )}
      </Space>
    </div>
  );

  const itemLabel = (key) => (REPAIR_ITEMS.find((i) => i.key === key) || {}).label || key;

  const renderConfirm = () => {
    const fee = config?.fee ?? null;
    const shippingFee = config?.shipping_fee ?? null;
    const freeFirst = config?.free_first;
    return (
      <div>
        <Title level={4} style={{ textAlign: 'center', marginBottom: 16 }}>確認與備註</Title>

        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <Text type="secondary">訂單來源</Text>
            <Text>{source === 'site' ? '本站訂單' : 'Pinkoi 訂單'}・{sourceOrderCode}</Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, gap: 12 }}>
            <Text type="secondary">維修項目</Text>
            <Text style={{ textAlign: 'right' }}>{repairItems.map(itemLabel).join('、')}</Text>
          </div>
          {repairItems.includes('resize') && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text type="secondary">手圍調整</Text>
              <Text>{currentWrist} cm → {targetWrist} cm</Text>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <Text type="secondary">寄回方式</Text>
            <Text style={{ textAlign: 'right' }}>
              {shipBackMethod === 'post'
                ? `郵局掛號・${recipientAddress}`
                : `超商店到店・${CHAIN_LABEL[cvsSubType] || ''} ${cvsStore?.name || ''}`}
            </Text>
          </div>
        </Card>

        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 13 }}>備註（選填）</Text>
          <TextArea
            style={{ marginTop: 6 }}
            rows={3}
            placeholder="其他想告訴我們的事（例如問題發生的情形）"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            showCount
          />
        </div>

        {/* 費用提示（實際維修費免費與否由後端判定；全家寄件運費一律加收） */}
        <Alert
          type="info"
          showIcon
          message="費用說明"
          description={
            <div>
              {shippingFee != null && (
                <div>全家寄件運費 <Text strong>NT$ {shippingFee}</Text>（一律收取，含寄回運費）</div>
              )}
              {fee != null ? (
                <div style={{ marginTop: 4 }}>維修費 <Text strong>NT$ {fee}</Text>
                  {freeFirst && <Text type="secondary">（此訂單首次維修免收）</Text>}
                </div>
              ) : (
                <div style={{ marginTop: 4 }}>維修費將於送出後由系統計算。</div>
              )}
              {repairItems.includes('crystal') && (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary">水晶維修費於收件評估後，另以綠界連結通知補款。</Text>
                </div>
              )}
              <div style={{ marginTop: 6 }}>
                <Text type="secondary">送出後將導向綠界付款；付款完成即產生全家寄件單，供您到全家掃碼寄回。</Text>
              </div>
            </div>
          }
        />
      </div>
    );
  };

  // ==================== 主 render ====================
  return (
    <div style={containerStyle}>
      <div style={{ textAlign: 'center', paddingTop: 12, marginBottom: 12 }}>
        <Title level={3} style={{ margin: 0, color: 'var(--brand-primary)', fontWeight: 800, letterSpacing: 1 }}>
          <ToolOutlined style={{ marginRight: 8 }} />售後維修登記
        </Title>
      </div>

      <Steps
        current={currentStep}
        size="small"
        items={steps.map((s) => ({ title: isMobile ? '' : s.title }))}
        style={{ marginBottom: 24 }}
      />

      <div style={{ marginBottom: 24 }}>
        {currentKey === 'source' && renderSource()}
        {currentKey === 'items' && renderItems()}
        {currentKey === 'shipping' && renderShipping()}
        {currentKey === 'confirm' && renderConfirm()}
      </div>

      {nextHint && (
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 8, fontSize: 13 }}>
          {nextHint}
        </Text>
      )}

      <div style={{ display: 'flex', gap: 12 }}>
        {currentStep > 0 && (
          <Button size="large" icon={<ArrowLeftOutlined />} onClick={goPrev} style={{ flex: '0 0 auto' }}>
            上一步
          </Button>
        )}
        {currentKey !== 'confirm' ? (
          <Button
            type="primary" size="large" block
            disabled={!canNext}
            onClick={goNext}
            style={{ flex: 1, height: 48 }}
          >
            下一步 <ArrowRightOutlined />
          </Button>
        ) : (
          <Button
            type="primary" size="large" block
            loading={submitting}
            onClick={handleSubmit}
            style={{ flex: 1, height: 48 }}
          >
            送出維修申請
          </Button>
        )}
      </div>
    </div>
  );
}
